import * as React from 'react'
import { connect } from 'react-redux'

import Api, { ApiPromise, ApiResponse } from 'embed/services/Api'
import QueryParams from 'embed/services/QueryParams'

import { loadConfigBegin, loadConfigSuccess } from 'embed/actions'
import { brandFromHost, isLocalhost } from 'embed/constants/config'
import {
  captureGetCombinedConfigError,
  captureGetPartnerConfigError,
  captureInvalidPartnerConfigResponseWarning,
  capturePartnerConfigError,
} from 'embed/constants/errors'

import { usePrevious } from 'embed/hooks'
import { getThemeExtended } from 'embed/utils/config/themeConfig'
import { DEFAULT_CONFIG } from 'embed/constants/partner-config'
import {
  Brand,
  ClientTags,
  Config,
  ConfigKeys,
  ConfigProductToProduct,
  ConfigProductType,
  ConfigServiceDataKeys,
  LoanPurposeOptions,
  ParentData,
  PartnerConfigResponse,
  Product,
} from '@evenfinancial/even-ts-static'

export interface Props {
  children: React.ReactNode
  defaultConfig?: Config
  dispatchLoadConfigBegin: () => void
  dispatchLoadConfigSuccess: (config: Config) => void
  hasLoadedInitialPartnerConfig: boolean
  isLoadingParentConfig: boolean
  isLoadingPartnerConfig: boolean
  parentConfig?: ParentData
  partner?: string
  selectedProduct?: Product
  waitForParentConfig?: boolean
}

type LoadedConfigs = { [P in keyof Product]?: Config }

// Type guard to check if data is a valid PartnerConfigResponse
const isPartnerConfigResponse = (data: object): data is PartnerConfigResponse => {
  let isValid = true
  Object.keys(data).forEach((product) => {
    if (ConfigProductToProduct[product] === undefined || !Array.isArray(data[product])) {
      isValid = false
    }
  })

  return isValid
}

const getDefaultProductType = (queryParams: Config, firstProductConfigKey: Product): Product =>
  queryParams.product ?? queryParams.productType ?? queryParams.available_products?.[0] ?? firstProductConfigKey

const getCurrentProductType = (selectedProduct: Product, updatedLoadedConfigs: LoadedConfigs): Product =>
  selectedProduct ?? getDefaultProductType(getQueryParams(), Object.keys(updatedLoadedConfigs)[0] as Product)

export const partnerConfigIfBrandMatchesDomain = (config?: Config, brand?: Brand) => {
  // Return the partner config only if its `brand` matches the domain we're on
  if ((brand !== undefined && config?.[ConfigServiceDataKeys.brand] === brand) || isLocalhost) {
    return config ?? {}
  } else {
    return {}
  }
}

const isForConsumer = (): boolean => (getQueryParams()?.consumer ? true : false)

export const partnerApiUrl = (partner?: string): string => `/partnerpage/pages/${partner}/all`

const getQueryParams = () => {
  const queryParams = QueryParams.getAll()

  // Parse tag.* query params in to an object 'clientTags' so it can be correctly merged with any client
  // tags that come back from the the config service
  const queryParamClientTags = QueryParams.getClientTags(queryParams as ClientTags)

  return {
    ...queryParams,
    [ConfigKeys.clientTags]: queryParamClientTags,
  }
}

export const mergeConfigs = (
  defaultConfig: Config = DEFAULT_CONFIG,
  partnerConfig: Config = {},
  parentConfig: ParentData = { referrer: '' },
  queryParams: Config = {},
): Config => {
  const mergedConfig = {
    ...defaultConfig,
    ...partnerConfig,
    ...parentConfig,
    ...queryParams,
  }

  // For clientTags, we want to merge the objects, not have them be completely replaced by the next one
  // Note: if we end up doing this for any additional config properties, build a helper method to do this
  const mergedClientTags = {
    ...defaultConfig[ConfigKeys.clientTags],
    ...partnerConfig[ConfigKeys.clientTags],
    ...parentConfig[ConfigKeys.clientTags],
    ...queryParams[ConfigKeys.clientTags],
  }

  if (Object.keys(mergedClientTags).length > 0) {
    mergedConfig[ConfigKeys.clientTags] = mergedClientTags
  }

  if (mergedConfig[ConfigServiceDataKeys.loanPurposeOptions]) {
    let loanPurposeOptions = mergedConfig[ConfigServiceDataKeys.loanPurposeOptions]

    if (typeof loanPurposeOptions === 'string') {
      loanPurposeOptions = (loanPurposeOptions as string).split(',') as LoanPurposeOptions[]
    }

    if (loanPurposeOptions?.length === 1) {
      mergedConfig[ConfigKeys.defaultLoanPurpose] = loanPurposeOptions[0]
    }

    mergedConfig[ConfigKeys.loanPurposeOptions] = (
      loanPurposeOptions?.indexOf('' as LoanPurposeOptions) === -1 && loanPurposeOptions.length > 1
        ? ['', ...loanPurposeOptions]
        : loanPurposeOptions
    ) as LoanPurposeOptions[]
  }

  const themeExtended = getThemeExtended(defaultConfig, partnerConfig, queryParams)

  return { ...mergedConfig, ...themeExtended }
}
const fetchPartnerConfig = (partner?: string): ApiPromise<PartnerConfigResponse> =>
  Api.get(partnerApiUrl(partner)).then(
    async (response) =>
      new Promise<ApiResponse<PartnerConfigResponse>>((resolve, reject) => {
        if (isPartnerConfigResponse(response.data)) {
          resolve(response)
        } else {
          captureInvalidPartnerConfigResponseWarning(response.data)
          reject()
        }
      }),
  )

const getMappedProductType = (selectedProduct: Product, updatedLoadedConfigs): Product => {
  const currentProductType = getCurrentProductType(selectedProduct, updatedLoadedConfigs)
  const configProductTypes: string[] = Object.values(ConfigProductType)
  return configProductTypes.includes(currentProductType)
    ? ConfigProductToProduct[currentProductType]
    : currentProductType
}

const handleGetPartnerConfigError = (error, selectedProduct: Product, currentPartner: string) => {
  if (error?.response?.status === 404 && selectedProduct) {
    // If the requested partner config doesn't exist, instead fetch the default partner config for this brand (even / fiona)
    return fetchPartnerConfig(DEFAULT_CONFIG.partner).then(
      (response1) => response1.data?.[`${ConfigProductToProduct[selectedProduct]}`][0] ?? {},
    )
  } else {
    // Return any other errors (which will cause getCombinedConfig above to fall back to the static default config / query params)
    capturePartnerConfigError(error, partnerApiUrl(currentPartner))
    return Promise.reject(error)
  }
}

const ConfigProvider = ({
  children,
  defaultConfig,
  dispatchLoadConfigBegin,
  dispatchLoadConfigSuccess,
  hasLoadedInitialPartnerConfig,
  isLoadingParentConfig,
  isLoadingPartnerConfig,
  parentConfig,
  partner,
  selectedProduct,
}) => {
  const [hasLoadedConfig, setHasLoadedConfig] = React.useState(false)
  const [loadedConfigs, setLoadedConfigs] = React.useState({} as LoadedConfigs)

  const previousSelectedProduct = usePrevious(selectedProduct)

  React.useEffect(() => {
    getCombinedConfig().catch(captureGetCombinedConfigError)
  }, [])

  React.useEffect(() => {
    if (previousSelectedProduct !== undefined) {
      getCombinedConfig().catch(captureGetCombinedConfigError)
    }
  }, [selectedProduct])

  const getCombinedConfig = async () => {
    const queryParams: Config = getQueryParams()
    const currentPartner = queryParams.partner || partner
    let combinedConfig: Config

    dispatchLoadConfigBegin()

    try {
      const partnerConfig = (await getPartnerConfig(currentPartner)) as Config
      combinedConfig = mergeConfigs(defaultConfig, partnerConfig, parentConfig, queryParams)
    } catch (error) {
      // If the request fails, just use the combination of the default config and the query params
      combinedConfig = mergeConfigs(defaultConfig, queryParams)
      captureGetPartnerConfigError(error)
    }
    // If the request is for consumer, delete the headerText value since consumer displays it already.
    if (isForConsumer()) {
      combinedConfig.headerText = undefined
    }
    dispatchLoadConfigSuccess(combinedConfig)
  }

  const getPartnerConfig = async (currentPartner?: string): Promise<ApiResponse<PartnerConfigResponse> | {}> => {
    if (selectedProduct && hasLoadedConfig) {
      // If we've previously loaded the config for this product, return it immediately
      return Promise.resolve(partnerConfigIfBrandMatchesDomain(loadedConfigs[selectedProduct], brandFromHost))
    }
    if (currentPartner) {
      // If we haven't loaded it yet, try to fetch it and return
      try {
        const response = await fetchPartnerConfig(currentPartner)
        const updatedLoadedConfigs = { ...loadedConfigs }
        // The list of available products are the product keys returned by the partner config endpoint
        const availableProducts = Object.keys(response.data).map((productType) => ConfigProductToProduct[productType])
        Object.keys(response.data).forEach((productType) => {
          // Note: there can be multiple configs for a single product type, we just take the first one.
          const configForProduct = response.data[productType][0]

          // Set the availableProducts for each of the individual configs
          configForProduct[ConfigKeys.AvailableProducts] = availableProducts
          updatedLoadedConfigs[ConfigProductToProduct[productType]] = configForProduct
        })
        setHasLoadedConfig(true)
        setLoadedConfigs(updatedLoadedConfigs)

        const mappedProductType = getMappedProductType(selectedProduct, updatedLoadedConfigs)

        const config = {
          ...partnerConfigIfBrandMatchesDomain(updatedLoadedConfigs[mappedProductType], brandFromHost),
        }
        return config
      } catch (error) {
        return handleGetPartnerConfigError(error, selectedProduct, currentPartner)
      }
    }
    return Promise.resolve({})
  }

  return hasLoadedInitialPartnerConfig && !isLoadingPartnerConfig && !isLoadingParentConfig ? children : null
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  dispatchLoadConfigBegin: () => dispatch(loadConfigBegin()),
  dispatchLoadConfigSuccess: ownProps.dispatchLoadConfigSuccess || ((config) => dispatch(loadConfigSuccess(config))),
})

const mapStateToProps = (state, ownProps) => ({
  hasLoadedInitialPartnerConfig: state.ui.hasLoadedInitialPartnerConfig,
  isLoadingParentConfig: ownProps.waitForParentConfig === true && (state?.parent?.loading ?? true),
  isLoadingPartnerConfig: state.ui.isLoadingPartnerConfig,
  parentConfig: state.parent ?? {},
  selectedProduct: state.ui.selectedProduct,
})

export default connect(mapStateToProps, mapDispatchToProps)(ConfigProvider)
